import React, { useState, useEffect } from 'react';
import { Container, Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SlidingMenu from '../components/SlidingMenu';
import VersionFooter from '../components/VersionFooter';
import apiCall from '../shared/apiCall';

export default function CopyItinerary() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [shareId, setShareId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [copiedPlanId, setCopiedPlanId] = useState(null);

  // Check if shareId is in URL params
  useEffect(() => {
    const urlShareId = searchParams.get('shareId');
    if (urlShareId) {
      setShareId(urlShareId);
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!shareId || !shareId.trim()) {
      setError(t('copyItinerary.errors.noShareId', { defaultValue: 'Please enter a Plan ID' }));
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccess(false);

    try {
      const response = await apiCall('/api/shared', {
        action: 'COPY_SHARED_PLAN',
        shareId: shareId.trim()
      }, 'POST');

      if (response.success) {
        setSuccess(true);
        setCopiedPlanId(response.planId);
        setShareId(''); // Clear the input
        
        // Redirect to the new plan after a short delay
        setTimeout(() => {
          navigate(`/plan-detail/${response.planId}`);
        }, 2000);
      } else {
        setError(response.message || t('copyItinerary.errors.copyFailed', { defaultValue: 'Failed to copy itinerary. Please check the Plan ID and try again.' }));
      }
    } catch (err) {
      console.error('Error copying itinerary:', err);
      setError(t('copyItinerary.errors.generic', { defaultValue: 'An error occurred while copying the itinerary. Please try again.' }));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <SlidingMenu title={t('copyItinerary.title', { defaultValue: 'Copy Itinerary' })} />
      <Container className="py-4">
        <Card className="shadow-sm">
          <Card.Body className="p-4">
            <h4 className="mb-4">
              <i className="bi bi-copy me-2"></i>
              {t('copyItinerary.title', { defaultValue: 'Copy Itinerary' })}
            </h4>
            <p className="text-muted mb-4">
              {t('copyItinerary.description', { defaultValue: 'Paste the Plan ID from a shared itinerary to copy it to your account.' })}
            </p>
            <div className="bg-light p-3 rounded mb-4 small text-muted">
              <p className="mb-2">
                {t('copyItinerary.copyAsIs', { defaultValue: "This feature copies the itinerary as is, but don't worry—it's easy to change dates to your dates." })}
              </p>
              <p className="mb-0">
                {t('copyItinerary.howToChangeDates', { defaultValue: 'Just go to the "Overview" tab and at the "Trip details" top section you\'ll see the "Edit dates" button.' })}
              </p>
            </div>

            {success && copiedPlanId && (
              <Alert variant="success" className="mb-3">
                <i className="bi bi-check-circle me-2"></i>
                {t('copyItinerary.success', { defaultValue: 'Itinerary copied successfully! Redirecting to your new plan...' })}
              </Alert>
            )}

            {error && (
              <Alert variant="danger" className="mb-3">
                <i className="bi bi-exclamation-circle me-2"></i>
                {error}
              </Alert>
            )}

            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">
                  {t('copyItinerary.planIdLabel', { defaultValue: 'Plan ID' })}
                </Form.Label>
                <Form.Control
                  type="text"
                  value={shareId}
                  onChange={(e) => setShareId(e.target.value)}
                  placeholder={t('copyItinerary.planIdPlaceholder', { defaultValue: 'Paste the Plan ID here' })}
                  disabled={isLoading}
                  className="font-monospace"
                />
                <Form.Text className="text-muted">
                  {t('copyItinerary.planIdHelp', { defaultValue: 'You can find the Plan ID in the "Copy Itinerary" popup on the shared plan page.' })}
                </Form.Text>
              </Form.Group>

              <div className="d-flex gap-2">
                <Button
                  type="submit"
                  variant="primary"
                  disabled={isLoading || !shareId.trim()}
                >
                  {isLoading ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      {t('copyItinerary.copying', { defaultValue: 'Copying...' })}
                    </>
                  ) : (
                    <>
                      <i className="bi bi-copy me-2"></i>
                      {t('copyItinerary.copyButton', { defaultValue: 'Copy Itinerary' })}
                    </>
                  )}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => navigate('/')}
                >
                  {t('copyItinerary.cancel', { defaultValue: 'Cancel' })}
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </Container>
      <VersionFooter />
    </>
  );
}
